import axios from 'axios';

import { auth, firebaseAuth } from '../firebase';
import { getActiveWorkspaceId } from '@/stores/workspace-store';
import { markStepIfNeeded } from '@/lib/api/onboarding';

const API_BASE = import.meta.env.VITE_API_URL;

interface CreateProjectData {
  name: string;
  description?: string;
  templateId?: string;
  mode?: 'lite' | 'power';
  settings?: Record<string, any>;
}

interface SaveFileData {
  content: string;
}

interface CreateFileData {
  content?: string;
}

interface CreateVersionData {
  message: string;
}

interface Template {
  id: string;
  name: string;
  description?: string;
  category: string;
  features: string[];
  dataSources: string[];
  thumbnailUrl?: string;
  ociPath: string;
  appPort?: string; // Port for preview (e.g., "5173" for Vite)
  usageCount: number;
  metadata: {
    version?: string;
    author?: string;
    framework?: string;
    repoUrl?: string;
  };
  config?: {
    buildCommand?: string;
    startCommand?: string;
    installCommand?: string;
    appPort?: string; // Port for preview (e.g., "5173" for Vite)
    envVariables?: Array<{
      key: string;
      description: string;
      required: boolean;
      defaultValue?: string;
    }>;
  };
  tags: string[];
}

interface CloneTemplateData {
  projectName?: string;
  mode?: 'lite' | 'power';
  devContainerId?: string;
  useDefaultDevContainer?: boolean;
  folderId?: string;
}

class EditorAPI {
  private client = axios.create({
    baseURL: `${API_BASE}/api/editor`,
  });

  constructor() {
    // Add auth + workspace interceptor
    this.client.interceptors.request.use(async (config) => {
      const token = await auth.getIdToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      // Add workspace context from store
      const workspaceId = getActiveWorkspaceId();
      if (workspaceId) {
        config.headers['X-Workspace-Id'] = workspaceId;
      }
      return config;
    });
  }

  // Projects
  async createProject(data: CreateProjectData) {
    const res = await this.client.post('/projects', data);
    // Fire-and-forget onboarding step 1 mark
    const token = await auth.getIdToken();
    const uid = firebaseAuth.currentUser?.uid;
    if (token && uid) markStepIfNeeded('step1', token, uid);
    return res.data;
  }

  async getProject(id: string) {
    const res = await this.client.get(`/projects/${id}`);
    return res.data;
  }

  async listProjects(filters?: { search?: string; folderId?: string | null }) {
    const params = new URLSearchParams();
    if (filters?.search) {
      params.append('search', filters.search);
    }
    if (filters?.folderId !== undefined) {
      params.append('folderId', filters.folderId === null ? 'root' : filters.folderId);
    }
    const queryString = params.toString();
    const url = queryString ? `/projects?${queryString}` : '/projects';
    const res = await this.client.get(url);
    return res.data;
  }

  async getRecentProjects(limit: number = 5): Promise<any[]> {
    const res = await this.client.get(`/projects/recent?limit=${limit}`);
    return res.data;
  }

  async updateProject(id: string, data: Partial<CreateProjectData>) {
    const res = await this.client.put(`/projects/${id}`, data);
    return res.data;
  }

  async deleteProject(id: string) {
    const res = await this.client.delete(`/projects/${id}`);
    return res.data;
  }

  async getProjectDevContainerStatus(id: string) {
    const res = await this.client.get(`/projects/${id}/dev-container-status`);
    return res.data;
  }

  /**
   * Get dev container status changes since a timestamp (delta sync)
   * Used for efficient polling - single call per workspace instead of N calls per project
   * @param since - Unix timestamp (ms). Server adds 60s buffer to avoid gaps.
   * @returns { changes: DevContainerStatusChange[], serverTime: number }
   */
  async getDevContainerStatusChanges(since?: number) {
    const params = since ? `?since=${since}` : '';
    const res = await this.client.get(`/projects/dev-container-status-changes${params}`);
    return res.data as {
      changes: Array<{
        hasDevContainer: boolean;
        devContainerId: string;
        status: string;
        subdomain: string;
        devContainerUrl: string | null;
        specs: Record<string, unknown>;
        lastAccessedAt: string | null;
        createdAt: string;
      }>;
      serverTime: number;
    };
  }

  // Files
  async getFileTree(projectId: string) {
    const res = await this.client.get(`/projects/${projectId}/files`);
    return res.data;
  }

  async getFileContent(projectId: string, filePath: string) {
    const res = await this.client.get(`/projects/${projectId}/files/${encodeURIComponent(filePath)}`);
    return res.data;
  }

  async saveFile(projectId: string, filePath: string, content: string) {
    const res = await this.client.put(
      `/projects/${projectId}/files/${encodeURIComponent(filePath)}`,
      { content }
    );
    return res.data;
  }

  async createFile(projectId: string, filePath: string, content: string = '') {
    const res = await this.client.post(
      `/projects/${projectId}/files/${encodeURIComponent(filePath)}`,
      { content }
    );
    return res.data;
  }

  async deleteFile(projectId: string, filePath: string) {
    const res = await this.client.delete(
      `/projects/${projectId}/files/${encodeURIComponent(filePath)}`
    );
    return res.data;
  }

  // Versions
  async getVersionHistory(projectId: string) {
    const res = await this.client.get(`/projects/${projectId}/versions`);
    return res.data;
  }

  async createVersion(projectId: string, message: string) {
    const res = await this.client.post(`/projects/${projectId}/versions`, { message });
    return res.data;
  }

  async getVersion(projectId: string, versionId: string) {
    const res = await this.client.get(`/projects/${projectId}/versions/${versionId}`);
    return res.data;
  }

  // Templates
  async listTemplates(filters?: {
    category?: string;
    dataSources?: string[];
    search?: string;
  }) {
    const params = new URLSearchParams();
    if (filters?.category) {
      params.append('category', filters.category);
    }
    if (filters?.dataSources?.length) {
      params.append('dataSources', filters.dataSources.join(','));
    }
    if (filters?.search) {
      params.append('search', filters.search);
    }
    
    const res = await this.client.get<Template[]>(`/templates?${params}`);
    return res.data;
  }

  async getTemplate(templateId: string) {
    const res = await this.client.get<Template>(`/templates/${templateId}`);
    return res.data;
  }

  async cloneTemplate(templateId: string, data: CloneTemplateData) {
    const res = await this.client.post(`/templates/${templateId}/clone`, data);
    // Fire-and-forget onboarding step 1 mark
    const token = await auth.getIdToken();
    const uid = firebaseAuth.currentUser?.uid;
    if (token && uid) markStepIfNeeded('step1', token, uid);
    return res.data;
  }

  async getTemplateStats() {
    const res = await this.client.get('/templates/stats');
    return res.data;
  }

  async suggestProjectName() {
    const res = await this.client.get('/projects/suggest-name');
    return res.data;
  }

  async getUserDevContainers() {
    const res = await this.client.get('/containers/workspaces');
    return res.data;
  }

  async getDefaultDevContainer() {
    const res = await this.client.get('/containers/workspaces/default');
    return res.data;
  }

  async setDefaultDevContainer(devContainerId: string) {
    const res = await this.client.post(`/containers/workspaces/${devContainerId}/set-default`);
    return res.data;
  }

  // Move project to folder (null = root)
  async moveProjectToFolder(projectId: string, folderId: string | null) {
    const res = await this.client.patch(`/projects/${projectId}/move-to-folder`, { folderId });
    return res.data;
  }

  // Folders
  async listFolders() {
    const res = await this.client.get('/folders');
    return res.data;
  }

  async createFolder(name: string) {
    const res = await this.client.post('/folders', { name });
    return res.data;
  }

  async renameFolder(id: string, name: string) {
    const res = await this.client.patch(`/folders/${id}`, { name });
    return res.data;
  }

  async deleteFolder(id: string) {
    const res = await this.client.delete(`/folders/${id}`);
    return res.data;
  }

  // Environment sync (connections + secrets)
  async syncProjectEnvironment(projectId: string) {
    const res = await this.client.post(`/projects/${projectId}/sync-environment`);
    return res.data;
  }

  // List project files via sidecar
  async listProjectFiles(projectId: string): Promise<string[]> {
    const res = await this.client.get(`/projects/${projectId}/file-tree`);
    return res.data;
  }

  // Cross-project referencing
  async searchReferenceableProjects(query?: string, excludeProjectId?: string) {
    const params = new URLSearchParams();
    if (query) params.append('q', query);
    if (excludeProjectId) params.append('excludeProjectId', excludeProjectId);
    const res = await this.client.get(`/projects/search-referenceable?${params}`);
    return res.data;
  }
}

export const editorAPI = new EditorAPI();